
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Send, X, Users, Building } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useReferrals } from '@/hooks/use-referrals';

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'customer' | 'studio';
  studioName?: string;
  studioEmail?: string;
}

const ReferralModal: React.FC<ReferralModalProps> = ({
  isOpen,
  onClose,
  initialTab = 'customer',
  studioName,
  studioEmail,
}) => {
  const [activeTab, setActiveTab] = useState<'customer' | 'studio'>(initialTab);
  const [email, setEmail] = useState(studioEmail || '');
  const [studioNameInput, setStudioNameInput] = useState(studioName || '');
  const { sendReferral } = useReferrals();
  const [isLoading, setIsLoading] = useState(false);

  const handleTabChange = (value: string) => {
    setActiveTab(value as 'customer' | 'studio');
    setEmail(value === 'studio' && studioEmail ? studioEmail : '');
    setStudioNameInput(value === 'studio' && studioName ? studioName : '');
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleInvite = async () => {
    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);

    try {
      await sendReferral(email, activeTab, activeTab === 'studio' ? studioNameInput : undefined);
      setEmail('');
      setStudioNameInput('');
      onClose();
    } catch (error) {
      console.error('Failed to send referral:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-chrona-gray-900 border border-chrona-gray-700 shadow-xl shadow-chrona-accent/5">
        <DialogHeader>
          <DialogTitle className="text-chrona-text">Freunde einladen</DialogTitle>
          <DialogDescription className="text-chrona-gray-300">
            Lade Freunde oder Studios zu CHRONA ein und sichere dir Vorteile.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="customer" className="flex items-center gap-2">
              <Users size={16} />
              <span>Freunde</span>
            </TabsTrigger>
            <TabsTrigger value="studio" className="flex items-center gap-2">
              <Building size={16} />
              <span>Studios</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="customer" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="friend-email" className="text-chrona-gray-200">
                E-Mail-Adresse
              </Label>
              <Input 
                id="friend-email" 
                placeholder="freund@example.com" 
                className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="bg-chrona-accent/10 p-4 rounded-lg border border-chrona-accent/20">
              <h3 className="font-medium text-chrona-accent mb-1">Gratis-Monat sichern!</h3>
              <p className="text-sm text-chrona-gray-300">
                Für jede erfolgreiche Einladung erhältst du einen Gratis-Monat CHRONA Premium, wenn dein Freund sich registriert.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="studio" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="studio-name" className="text-chrona-gray-200">
                Studio-Name (optional)
              </Label>
              <Input 
                id="studio-name" 
                placeholder="Beauty Studio" 
                className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text"
                value={studioNameInput}
                onChange={(e) => setStudioNameInput(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="studio-email" className="text-chrona-gray-200">
                E-Mail-Adresse des Studios
              </Label>
              <Input 
                id="studio-email" 
                placeholder="studio@example.com" 
                className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="bg-chrona-accent/10 p-4 rounded-lg border border-chrona-accent/20">
              <h3 className="font-medium text-chrona-accent mb-1">Doppelter Gratis-Monat!</h3>
              <p className="text-sm text-chrona-gray-300">
                Für jedes erfolgreich eingeladene Studio erhältst du zwei Gratis-Monate CHRONA Premium.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex sm:justify-between gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-chrona-gray-700 hover:bg-chrona-gray-800 text-chrona-gray-300"
          >
            <X className="h-4 w-4 mr-2" />
            Abbrechen
          </Button>
          
          <Button
            onClick={handleInvite}
            disabled={isLoading || !email}
            className="bg-chrona-accent hover:bg-chrona-accent-light text-white"
          >
            {isLoading ? (
              <span className="animate-spin mr-2">◌</span>
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Einladung senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReferralModal;
